/**
 * @fileoverview Serviço Supabase para catálogo de produtos/serviços.
 *
 * Observação:
 * - O CRM é "adaptável": o catálogo é um acelerador (defaults).
 * - No deal, ainda permitimos itens personalizados (product_id pode ser NULL em deal_items).
 */

import { supabase } from './client';
import { Product } from '@/types';
import { sanitizeUUID } from './utils';

// =============================================================================
// Organization inference (client-side, RLS-safe)
// =============================================================================
let cachedOrgId: string | null = null;
let cachedOrgUserId: string | null = null;

async function getCurrentOrganizationId(): Promise<string | null> {
  if (!supabase) return null;

  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;

  if (cachedOrgUserId === user.id && cachedOrgId) return cachedOrgId;

  const { data: profile, error } = await supabase
    .from('profiles')
    .select('organization_id')
    .eq('id', user.id)
    .maybeSingle();

  if (error) return null;

  const orgId = sanitizeUUID((profile as any)?.organization_id);
  cachedOrgUserId = user.id;
  cachedOrgId = orgId;
  return orgId;
}

type DbProduct = {
  id: string;
  organization_id: string | null;
  name: string;
  description: string | null;
  price: number;
  sku: string | null;
  active: boolean | null;
  created_at: string;
  updated_at: string;
  owner_id: string | null;
  target_purpose: string | null;
  price_min: number | null;
  price_max: number | null;
  unit_types: string[] | null;
  keywords: string[] | null;
};

const SELECT_FIELDS = 'id, organization_id, name, description, price, sku, active, created_at, updated_at, owner_id, target_purpose, price_min, price_max, unit_types, keywords';

function transformProduct(db: DbProduct): Product {
  return {
    id: db.id,
    organizationId: db.organization_id || undefined,
    name: db.name,
    description: db.description || undefined,
    price: Number(db.price ?? 0),
    sku: db.sku || undefined,
    active: db.active ?? true,
    targetPurpose: (db.target_purpose as Product['targetPurpose']) ?? null,
    priceMin: db.price_min ?? null,
    priceMax: db.price_max ?? null,
    unitTypes: db.unit_types ?? [],
    keywords: db.keywords ?? [],
  };
}

type CreateInput = {
  name: string;
  price: number;
  sku?: string;
  description?: string;
  targetPurpose?: 'investimento' | 'moradia' | 'ambos' | null;
  priceMin?: number | null;
  priceMax?: number | null;
  unitTypes?: string[];
  keywords?: string[];
};

type UpdateInput = Partial<CreateInput & { active: boolean }>;

export const productsService = {
  async getAll(): Promise<{ data: Product[]; error: Error | null }> {
    try {
      if (!supabase) return { data: [], error: new Error('Supabase não configurado') };

      const { data, error } = await supabase
        .from('products')
        .select(SELECT_FIELDS)
        .order('created_at', { ascending: false });

      if (error) return { data: [], error };
      return { data: (data || []).map(r => transformProduct(r as DbProduct)), error: null };
    } catch (e) {
      return { data: [], error: e as Error };
    }
  },

  async getActive(): Promise<{ data: Product[]; error: Error | null }> {
    try {
      if (!supabase) return { data: [], error: new Error('Supabase não configurado') };

      const { data, error } = await supabase
        .from('products')
        .select(SELECT_FIELDS)
        .eq('active', true)
        .order('created_at', { ascending: false });

      if (error) return { data: [], error };
      return { data: (data || []).map(r => transformProduct(r as DbProduct)), error: null };
    } catch (e) {
      return { data: [], error: e as Error };
    }
  },

  async create(input: CreateInput): Promise<{ data: Product | null; error: Error | null }> {
    try {
      if (!supabase) return { data: null, error: new Error('Supabase não configurado') };

      const { data: { user } } = await supabase.auth.getUser();
      const organizationId = await getCurrentOrganizationId();

      const { data, error } = await supabase
        .from('products')
        .insert({
          name: input.name,
          price: input.price,
          sku: input.sku || null,
          description: input.description || null,
          active: true,
          owner_id: sanitizeUUID(user?.id),
          organization_id: organizationId,
          target_purpose: input.targetPurpose ?? null,
          price_min: input.priceMin ?? null,
          price_max: input.priceMax ?? null,
          unit_types: input.unitTypes?.length ? input.unitTypes : null,
          keywords: input.keywords?.length ? input.keywords : null,
        })
        .select(SELECT_FIELDS)
        .single();

      if (error) return { data: null, error };
      return { data: transformProduct(data as DbProduct), error: null };
    } catch (e) {
      return { data: null, error: e as Error };
    }
  },

  async update(id: string, updates: UpdateInput): Promise<{ error: Error | null }> {
    try {
      if (!supabase) return { error: new Error('Supabase não configurado') };

      const payload: Record<string, unknown> = { updated_at: new Date().toISOString() };
      if (updates.name !== undefined) payload.name = updates.name;
      if (updates.price !== undefined) payload.price = updates.price;
      if (updates.sku !== undefined) payload.sku = updates.sku || null;
      if (updates.description !== undefined) payload.description = updates.description || null;
      if (updates.active !== undefined) payload.active = updates.active;
      if (updates.targetPurpose !== undefined) payload.target_purpose = updates.targetPurpose ?? null;
      if (updates.priceMin !== undefined) payload.price_min = updates.priceMin ?? null;
      if (updates.priceMax !== undefined) payload.price_max = updates.priceMax ?? null;
      if (updates.unitTypes !== undefined) payload.unit_types = updates.unitTypes?.length ? updates.unitTypes : null;
      if (updates.keywords !== undefined) payload.keywords = updates.keywords?.length ? updates.keywords : null;

      const { error } = await supabase
        .from('products')
        .update(payload)
        .eq('id', sanitizeUUID(id));

      return { error: error ?? null };
    } catch (e) {
      return { error: e as Error };
    }
  },

  async delete(id: string): Promise<{ error: Error | null }> {
    try {
      if (!supabase) return { error: new Error('Supabase não configurado') };
      const { error } = await supabase
        .from('products')
        .delete()
        .eq('id', sanitizeUUID(id));

      return { error: error ?? null };
    } catch (e) {
      return { error: e as Error };
    }
  },
};
